import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './App.css';
import { z } from 'zod';

const noTags = (v) => !v.includes('<') && !v.includes('>');
const leadSchema = z.object({
  name: z.string().trim().min(1, 'Введите имя').max(100, 'Слишком длинно').refine(noTags, 'Спецсимволы запрещены'),
  email: z.string().email('Некорректный email'),
  phone: z.string().trim().regex(/^\+7\d{10}$/, 'Введите корректный номер (+7XXXXXXXXXX)'),
  comment: z.string().trim().max(2000, 'Слишком длинно').optional().default('').refine(noTags, 'Спецсимволы запрещены'),
});

const sanitizeText = (v) => v.replace(/<[^>]*>?/gm, '');
const sanitizePhone = (v) => v.replace(/[^0-9+-\s()]/g, '').slice(0, 32);

const popupSchema = z.object({
  name: z.string().trim().min(1, 'Введите имя').max(100, 'Слишком длинно').refine(noTags, 'Спецсимволы запрещены'),
  email: z.string().email('Некорректный email'),
  phone: z.string().trim().regex(/^\+7\d{10}$/, 'Введите корректный номер (+7XXXXXXXXXX)'),
});


const PopupForm = ({ onClose }) => {
  const [popupData, setPopupData] = useState({ name: '', email: '', phone: '+7' });
  const [popupErrors, setPopupErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handlePhoneChange = (e) => {
    let value = e.target.value;
    // Allow only digits and ensure it starts with +7
    if (!value.startsWith('+7')) {
      value = '+7';
    }
    // Limit length
    value = '+7' + value.substring(2).replace(/\D/g, '').slice(0, 10);
    setPopupData(prev => ({ ...prev, phone: value }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPopupData(prev => ({ ...prev, [name]: sanitizeText(value) }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationResult = popupSchema.safeParse(popupData);
    if (!validationResult.success) {
      const errors = {};
      validationResult.error.issues.forEach(issue => {
        errors[issue.path[0]] = issue.message;
      });
      setPopupErrors(errors);
      setSubmitMessage('Проверьте поля формы.');
      return;
    }

    setPopupErrors({});
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // We need to send data compliant with the main `leadSchema`
      const fullData = {
        name: popupData.name,
        phone: popupData.phone,
        email: popupData.email,
        company_name: 'Заявка из pop-up', // Placeholder
        comment: 'Клиент заполнил форму во всплывающем окне.',
      };
      
      const response = await axios.post('/api/submit-lead', fullData);

      if (response.data.success) {
        window.location.replace(`${window.location.origin}/success.html`);
      } else {
        setSubmitMessage('Ошибка при отправке. Попробуйте еще раз.');
      }
    } catch (error) {
      setSubmitMessage('Сетевая ошибка. Попробуйте еще раз.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    // Block body scroll when popup is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="relative max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-white rounded-xl shadow-2xl p-8 w-full relative">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Готовы улучшить свой бизнес уже сегодня?</h3>
            <p className="text-gray-600 mb-6">Оставьте заявку, и мы немедленно свяжемся для демонстрации.</p>
          </div>
          <form onSubmit={handleSubmit} noValidate className="pointer-events-auto">
            <div className="space-y-4">
              <input type="text" name="name" placeholder="Имя" required value={popupData.name} onChange={handleChange} className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${popupErrors.name ? 'border-red-500' : 'border-gray-300'}`} />
              {popupErrors.name && <p className="text-red-600 text-sm">{popupErrors.name}</p>}
  
              <input type="email" name="email" placeholder="Email" required value={popupData.email} onChange={handleChange} className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${popupErrors.email ? 'border-red-500' : 'border-gray-300'}`} />
              {popupErrors.email && <p className="text-red-600 text-sm">{popupErrors.email}</p>}
              
              <input type="tel" name="phone" placeholder="Телефон" required value={popupData.phone} onChange={handlePhoneChange} className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${popupErrors.phone ? 'border-red-500' : 'border-gray-300'}`} />
              {popupErrors.phone && <p className="text-red-600 text-sm">{popupErrors.phone}</p>}
            </div>
            <button type="submit" disabled={isSubmitting} className="w-full mt-6 bg-primary-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50">
              {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
            </button>
            {submitMessage && <p className={`mt-4 text-center text-sm ${submitMessage.includes('Ошибка') ? 'text-red-600' : 'text-gray-600'}`}>{submitMessage}</p>}
          </form>
          <button 
            onClick={onClose} 
            className="w-full mt-4 py-2 px-4 text-gray-400 hover:text-gray-500 text-sm font-normal transition-colors opacity-70 hover:opacity-100"
            aria-label="Закрыть"
            type="button"
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};

const App = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '+7',
    comment: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [isHeaderSolid, setIsHeaderSolid] = useState(false);
  const heroRef = useRef(null);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState('64px');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 44000); // 44 seconds

    return () => clearTimeout(timer); // Cleanup the timer
  }, []);

  const validateAll = (data) => {
    const res = leadSchema.safeParse(data);
    if (res.success) {
      setErrors({});
      return true;
    }
    const map = {};
    res.error.issues.forEach(i => {
      const path = i.path[0];
      if (path && !map[path]) map[path] = i.message;
    });
    setErrors(map);
    return false;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let sanitized = value;
    if (name === 'phone') {
      // Маска для телефона: +7 и максимум 10 цифр
      if (!value.startsWith('+7')) {
        sanitized = '+7';
      } else {
        sanitized = '+7' + value.substring(2).replace(/\D/g, '').slice(0, 10);
      }
    } else if (['name', 'comment'].includes(name)) {
      sanitized = sanitizeText(value);
    }

    const next = { ...formData, [name]: sanitized };
    setFormData(next);
    setTouched(prev => ({ ...prev, [name]: true }));
    validateAll(next);
  };

  const onBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    validateAll(formData);
  };

  const errorClass = (name) =>
    touched[name] && errors[name] ? ' border-red-500 ring-2 ring-red-300' : '';

  const hpRef = useRef(null);
  const startTsRef = useRef(Date.now());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage('');
    // антибот: слишком быстро / honeypot заполнен
    const tooFast = Date.now() - startTsRef.current < 1500;
    const hpFilled = hpRef.current && hpRef.current.value;
    if (hpFilled || tooFast) {
      setSubmitMessage('Похоже на автоматическую отправку. Попробуйте ещё раз.');
      return;
    }

    const ok = validateAll(formData);
    if (!ok) {
      setSubmitMessage('Проверьте поля формы.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = { ...formData, company_name: 'Основная форма сайта' };
      const response = await axios.post('/api/submit-lead', payload);
      
      if (response.data.success) {
        setFormData({
          name: '',
          email: '',
          phone: '+7',
          comment: ''
        });
        const successUrl = `${window.location.origin}/success.html`;
        window.location.replace(successUrl);
        return;
      }
      setSubmitMessage('Ошибка при отправке заявки. Попробуйте еще раз.');
    } catch (error) {
      setSubmitMessage('Ошибка при отправке заявки. Попробуйте еще раз.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const scrollToForm = () => {
    document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' });
  };

  const inputClasses = "w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-[#2B2B33]";

  useEffect(() => {
    const updateHeaderHeight = () => {
      const h = headerRef.current ? headerRef.current.offsetHeight : 64;
      setHeaderHeight(`${h}px`);
    };
    const handleScroll = () => {
      const h = headerRef.current ? headerRef.current.offsetHeight : 64;
      const heroH = heroRef.current ? heroRef.current.offsetHeight : 0;
      const threshold = Math.max(0, heroH - h);
      setIsHeaderSolid(window.scrollY >= Math.max(16, threshold - 8));
    };
    updateHeaderHeight();
    handleScroll();
    window.addEventListener('resize', updateHeaderHeight);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const h = headerRef.current ? headerRef.current.offsetHeight : 64;
    setHeaderHeight(`${h}px`);
  }, [mobileOpen]);

  return (
    <div className="App">
      {showPopup && <PopupForm onClose={() => setShowPopup(false)} />}
      <header ref={headerRef} id="site-header" className={`fixed top-0 left-0 w-full z-50 transition-all duration-200 ${isHeaderSolid ? 'bg-white/85 backdrop-blur border-b border-gray-200 shadow-sm' : 'bg-white/20 backdrop-blur border-b border-white/10'}`}>
        <div className="container-custom py-3 md:py-4 flex items-center justify-between">
          <a href="#hero" onClick={() => setMobileOpen(false)} className={`font-bold ${isHeaderSolid ? 'text-gray-900' : 'text-white'}`}>NΞXT³</a>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <a href="#solution" className={`font-bold ${isHeaderSolid ? 'text-gray-800 hover:text-primary-600' : 'text-white/90 hover:text-white'}`}>Как это работает</a>
            <a href="#features" className={`font-bold ${isHeaderSolid ? 'text-gray-800 hover:text-primary-600' : 'text-white/90 hover:text-white'}`}>Возможности</a>
            <a href="#pricing" className={`font-bold ${isHeaderSolid ? 'text-gray-800 hover:text-primary-600' : 'text-white/90 hover:text-white'}`}>Цены</a>
            <a href="#faq" className={`font-bold ${isHeaderSolid ? 'text-gray-800 hover:text-primary-600' : 'text-white/90 hover:text-white'}`}>FAQ</a>
            <a href="#contact-form" className="btn-primary">Попробовать</a>
          </nav>
          <button
            aria-label="Меню"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-white/90 hover:bg-white/10"
            onClick={() => setMobileOpen((v) => !v)}
          >
            <span className="sr-only">Menu</span>
            {mobileOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" strokeWidth={2} stroke={isHeaderSolid ? '#111827' : '#ffffff'} className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke={isHeaderSolid ? '#111827' : '#ffffff'} className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
        {/* Mobile menu (animated, always mounted for smooth close) */}
        <div className="md:hidden">
          {/* Backdrop */}
          <div
            className={`fixed inset-x-0 bottom-0 z-40 transition-opacity duration-200 ${mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
            style={{ top: headerHeight }}
            onClick={() => setMobileOpen(false)}
          />
          {/* Panel */}
          <div
            className="fixed inset-x-0 z-50"
            style={{ top: headerHeight }}
          >
            <div className={`mx-3 rounded-xl shadow-2xl overflow-hidden transform transition-all duration-200 origin-top ${mobileOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 -translate-y-2 scale-95'}`}
                 >
              <nav className="flex flex-col py-2 bg-gradient-to-b from-[#667eea]/95 to-[#764ba2]/95 text-white backdrop-blur border border-white/20 divide-y divide-white/10">
                <a href="#solution" onClick={() => setMobileOpen(false)} className="px-4 py-3 hover:bg-white/10 font-bold">Как это работает</a>
                <a href="#features" onClick={() => setMobileOpen(false)} className="px-4 py-3 hover:bg-white/10 font-bold">Возможности</a>
                <a href="#pricing" onClick={() => setMobileOpen(false)} className="px-4 py-3 hover:bg-white/10 font-bold">Цены</a>
                <a href="#faq" onClick={() => setMobileOpen(false)} className="px-4 py-3 hover:bg-white/10 font-bold">FAQ</a>
                <div className="px-4 py-3">
                  <a href="#contact-form" onClick={() => setMobileOpen(false)} className="btn-primary inline-flex">Попробовать</a>
                </div>
              </nav>
            </div>
          </div>
        </div>
      </header>
      {/* Hero Section */}
      <section ref={heroRef} id="hero" className="hero-section gradient-bg text-white section-padding" style={{ paddingTop: `calc(${headerHeight} + 64px)`, scrollMarginTop: headerHeight }}>
        <div className="container-custom">
          {/* Title with line */}
          <div className="flex items-center justify-center gap-6 mb-16">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-center">
              NΞXT³ Бухгалтерия
            </h1>
          </div>
          <div className="h-1 bg-white/30 mb-12"></div>
          
          {/* Content */}
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-12">
            {/* Left column - Text and Button */}
            <div className="lg:w-1/2 flex flex-col justify-between">
              <div className="space-y-8 flex-grow">
                <h2 className="text-2xl md:text-3xl font-semibold text-white/90">
                  Больше, чем просто чат. Smart-CRM для вашей команды.
                </h2>
                <p className="text-base sm:text-lg md:text-xl italic font-light text-white/80">
                  Современное решение в Telegram
                </p>
              </div>
              
              {/* Button */}
              <div className="mt-12 lg:mt-0">
                <div className="inline-flex flex-col items-start">
                  <button onClick={scrollToForm} className="btn-primary">
                    Попробовать бесплатно
                  </button>
                </div>
              </div>
            </div>
            
            {/* Right column - Video */}
            <div className="lg:w-1/2 flex items-center justify-center">
              <video
                ref={videoRef}
                className="w-full rounded-lg shadow-2xl ring-1 ring-white/20"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster="/video-preview-poster.jpg"
              >
                <source src="/video-preview.mp4" type="video/mp4" />
                Ваш браузер не поддерживает видео.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section id="problems" className="section-padding bg-gray-50" style={{ scrollMarginTop: headerHeight }}>
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gray-800">
            Знакомый хаос?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="problem-card bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="text-4xl mb-4">💬</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Задачи теряются</h3>
              <p className="text-gray-600">
                Клиенты пишут в WhatsApp, Telegram, на почту... Важные детали и документы теряются в десятках чатов.
              </p>
            </div>
            <div className="problem-card bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="text-4xl mb-4">👀</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Постоянный контроль</h3>
              <p className="text-gray-600">
                Приходится вручную проверять, принята ли задача в работу и не забыл ли про нее исполнитель.
              </p>
            </div>
            <div className="problem-card bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="text-4xl mb-4">📱</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Клиентская база в опасности</h3>
              <p className="text-gray-600">
                Вся история общения с клиентами хранится в личных телефонах сотрудников. Если сотрудник уйдет, он может увести базу.
              </p>
            </div>
            <div className="problem-card bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Непрозрачная загрузка</h3>
              <p className="text-gray-600">
                Сложно понять, кто из сотрудников действительно загружен работой, а кто — нет.
              </p>
            </div>
          </div>
          <div className="text-center mt-12">
            <img 
              src="https://images.unsplash.com/photo-1709718499852-4d4aa8162047?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxvdmVyd2hlbG1lZCUyMG9mZmljZXxlbnwwfHx8fDE3NTI3NTcxNjN8MA&ixlib=rb-4.1.0&q=85"
              alt="Перегруженный офисный работник"
              className="w-full max-w-md mx-auto rounded-lg shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section id="solution" className="section-padding bg-white" style={{ scrollMarginTop: headerHeight }}>
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gray-800">
            Все задачи — в одном окне. Под полным контролем.
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-8">
                <div className="solution-step flex items-start space-x-4">
                  <div className="bg-primary-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">1</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Клиент ставит задачу</h3>
                    <p className="text-gray-600">
                      Клиент отправляет сообщение, фото, документ или голосовое в чат с ботом в любое время.
                    </p>
                  </div>
                </div>
                <div className="solution-step flex items-start space-x-4">
                  <div className="bg-primary-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">2</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Бот создает задачу</h3>
                    <p className="text-gray-600">
                      Бот мгновенно фиксирует обращение, создает новую задачу и уведомляет всех ваших исполнителей.
                    </p>
                  </div>
                </div>
                <div className="solution-step flex items-start space-x-4">
                  <div className="bg-primary-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">3</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Исполнитель берет в работу</h3>
                    <p className="text-gray-600">
                      Сотрудник нажимает одну кнопку, чтобы принять задачу. Клиент сразу видит, что его обращение в работе.
                    </p>
                  </div>
                </div>
                <div className="solution-step flex items-start space-x-4">
                  <div className="bg-primary-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">4</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Вы видите всю картину</h3>
                    <p className="text-gray-600">
                      Все задачи, статусы и переписка по ним доступны вам в едином окне. Статистика покажет, кто и как работает.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <button onClick={scrollToForm} className="btn-primary">
                  Начать наводить порядок
                </button>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHx0ZWFtJTIwY29sbGFib3JhdGlvbnxlbnwwfHx8fDE3NTI3NTcxOTN8MA&ixlib=rb-4.1.0&q=85"
                alt="Команда работает вместе"
                className="w-full rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section-padding bg-gray-50" style={{ scrollMarginTop: headerHeight }}>
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gray-800">
            Ваш личный менеджер, который работает 24/7
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="feature-card bg-white p-8 rounded-lg shadow-lg">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-xl font-semibold mb-4">Умная статистика</h3>
              <p className="text-gray-600">
                Анализируйте, сколько задач выполнено, кто из сотрудников самый быстрый, и где "бутылочное горлышко" в вашей работе.
              </p>
            </div>
            <div className="feature-card bg-white p-8 rounded-lg shadow-lg">
              <div className="text-4xl mb-4">🔔</div>
              <h3 className="text-xl font-semibold mb-4">Авто-напоминания</h3>
              <p className="text-gray-600">
                Бот сам вежливо напомнит исполнителям о просроченных или еще не принятых задачах. Вам больше не нужно быть "надсмотрщиком".
              </p>
            </div>
            <div className="feature-card bg-white p-8 rounded-lg shadow-lg">
              <div className="text-4xl mb-4">📁</div>
              <h3 className="text-xl font-semibold mb-4">Работа с любыми файлами</h3>
              <p className="text-gray-600">
                Принимайте от клиентов текст, фото счетов, PDF-документы и даже голосовые сообщения — все станет задачей.
              </p>
            </div>
            <div className="feature-card bg-white p-8 rounded-lg shadow-lg">
              <div className="text-4xl mb-4">👥</div>
              <h3 className="text-xl font-semibold mb-4">Неограниченное количество пользователей</h3>
              <p className="text-gray-600">
                Добавляйте всех своих сотрудников и клиентов без доплат. Растите, не думая о лимитах.
              </p>
            </div>
            <div className="feature-card bg-white p-8 rounded-lg shadow-lg">
              <div className="text-4xl mb-4">💬</div>
              <h3 className="text-xl font-semibold mb-4">Вся работа в Telegram</h3>
              <p className="text-gray-600">
                Не нужно устанавливать и изучать сложные программы. Вся команда работает в привычном и удобном мессенджере.
              </p>
            </div>
            <div className="feature-card bg-white p-8 rounded-lg shadow-lg">
              <div className="text-4xl mb-4">🔐</div>
              <h3 className="text-xl font-semibold mb-4">Надежное хранение данных</h3>
              <p className="text-gray-600">
                Вся история общения и файлы хранятся на вашем сервере, а не в личных чатах сотрудников.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="section-padding bg-white" style={{ scrollMarginTop: headerHeight }}>
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gray-800">
            Прозрачные и честные цены
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="pricing-card bg-white border-2 border-primary-200 p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold mb-4 text-primary-600">Настройка</h3>
              <div className="text-4xl font-bold mb-4 text-gray-800">25 000 ₽</div>
              <p className="text-gray-600 mb-6">
                Обязательный платеж. Включает полную настройку бота под вас, брендирование и обучение команды.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>✅ Настройка под вашу компанию</li>
                <li>✅ Брендирование</li>
                <li>✅ Обучение команды</li>
                <li>✅ Техническая поддержка настройки</li>
              </ul>
            </div>
            <div className="pricing-card bg-primary-600 text-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold mb-4">Ежемесячный платеж</h3>
              <div className="text-4xl font-bold mb-4">8 000 ₽ / месяц</div>
              <p className="text-gray-100 mb-6">
                Ежемесячная оплата за использование системы. Включает хостинг, техническую поддержку, обновления и неограниченное число пользователей.
              </p>
              <ul className="space-y-2 text-sm text-gray-100">
                <li>✅ Хостинг системы</li>
                <li>✅ Техническая поддержка</li>
                <li>✅ Регулярные обновления</li>
                <li>✅ Неограниченное число пользователей</li>
                <li>✅ Неограниченное число клиентов</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section-padding bg-gray-50" style={{ scrollMarginTop: headerHeight }}>
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gray-800">
            Остались вопросы?
          </h2>
          <div className="max-w-3xl mx-auto">
            {[
              {
                question: "Насколько это безопасно?",
                answer: "Полностью. Бот разворачивается на защищенном сервере Next³, доступ есть только у вас. Это надежнее, чем хранить данные в личных чатах."
              },
              {
                question: "Сможем ли мы настроить бота под себя?",
                answer: "Да, базовая настройка и брендирование включены в установку."
              },
              {
                question: "Что если нам не понравится?",
                answer: "Для этого и нужен бесплатный 7-дневный период. Вы ничего не теряете, если решите не продолжать."
              },
              {
                question: "Как происходит оплата?",
                answer: "После тестового периода мы выставляем счет. Оплатить можно по карте или на расчетный счет."
              }
            ].map((item, index) => (
              <div key={index} className="mb-4">
                <button
                  onClick={() => toggleAccordion(index)}
                  className="w-full text-left p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex justify-between items-center"
                >
                  <span className="text-lg font-semibold text-gray-800">{item.question}</span>
                  <span className="text-primary-600 text-xl">
                    {activeAccordion === index ? '−' : '+'}
                  </span>
                </button>
                {activeAccordion === index && (
                  <div className="mt-2 p-6 bg-gray-100 rounded-lg">
                    <p className="text-gray-700">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="contact-form" className="section-padding gradient-bg text-white" style={{ scrollMarginTop: headerHeight }}>
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Готовы избавиться от хаоса в задачах?
            </h2>
            <p className="text-xl text-gray-100">
              Попробуйте в деле Next³ - CRM для бухгалтерии. Первые 7 дней — бесплатно и без обязательств.
            </p>
          </div>
          
          <div className="max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="bg-white rounded-lg p-8 shadow-2xl" noValidate>
              {/* honeypot (скрытое поле) */}
              <input
                ref={hpRef}
                type="text"
                name="website"
                autoComplete="off"
                tabIndex="-1"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Имя *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    onBlur={onBlur}
                    required
                    maxLength={100}
                    className={inputClasses + errorClass('name')}
                  />
                  {touched.name && errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={onBlur}
                    required
                    className={inputClasses + errorClass('email')}
                  />
                  {touched.email && errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Телефон *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onBlur={onBlur}
                    required
                    maxLength={32}
                    minLength={5}
                    pattern="^[0-9+\-\s()]{5,32}$"
                    className={inputClasses + errorClass('phone')}
                  />
                  {touched.phone && errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>
              </div>
              
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Комментарий
                </label>
                <textarea
                  name="comment"
                  value={formData.comment}
                  onChange={handleInputChange}
                  onBlur={onBlur}
                  rows={4}
                  maxLength={2000}
                  className={inputClasses + errorClass('comment')}
                  placeholder="Расскажите о вашей компании и текущих задачах..."
                />
                {touched.comment && errors.comment && (
                  <p className="mt-1 text-sm text-red-600">{errors.comment}</p>
                )}
              </div>
              
              <div className="mt-8">
                <button
                  type="submit"
                  disabled={isSubmitting || Object.keys(errors).length > 0}
                  className="w-full bg-primary-600 text-white py-4 px-8 rounded-lg font-semibold text-lg hover:bg-primary-700 transition-colors duration-300 disabled:opacity-50"
                >
                  {isSubmitting ? 'Отправляем...' : 'Получить бесплатный доступ'}
                </button>
              </div>
              
              {submitMessage && (
                <div className={`mt-4 p-4 rounded-lg ${submitMessage.includes('успешно') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {submitMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="container-custom text-center">
          <p>&copy; 2024 Next³. Все права защищены.</p>
        </div>
      </footer>
    </div>
  );
};

export default App;